import { Controller } from "@hotwired/stimulus"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"
import { showBlock } from "lib/helpers"

const TYPE_LABELS = {
  notice: "Notice",
  warning: "Warning",
  error: "Alert",
}

const KIND_LABELS = {
  private: "Private",
  blocked: "Blocking",
  general: "General",
}

// account-managements--service-notifications on accounts management edit form.
export default class extends Controller {
  static targets = [
    "table",
    "item",
    "editModal",
    "editForm",
    "editErrors",
    "titleField",
    "messageField",
    "expirationField",
    "statusField",
    "typeField",
    "kindField",
    "confirmModal",
    "confirmName",
    "confirmErrors",
  ]

  connect() {
    this.pendingItem = null
    if (this.hasTableTarget) this.#initDataTable()
    this.#initDatepicker()
  }

  disconnect() {
    if (this.hasEditModalTarget) $(this.editModalTarget).modal("hide")
    if (this.hasConfirmModalTarget) $(this.confirmModalTarget).modal("hide")
    if (this.dataTable) {
      this.dataTable = destroyTurboDataTable(this.dataTable)
    }
    this.#destroyDatepicker()
    this.pendingItem = null
  }

  openEdit(event) {
    event.preventDefault()
    event.currentTarget.blur()
    const item = this.#itemFromEvent(event)
    if (!item || !this.hasEditFormTarget) return

    this.pendingItem = item
    this.editFormTarget.action = item.dataset.url
    if (this.hasTitleFieldTarget) this.titleFieldTarget.value = item.dataset.title || ""
    if (this.hasMessageFieldTarget) this.messageFieldTarget.value = item.dataset.message || ""
    if (this.hasExpirationFieldTarget) this.expirationFieldTarget.value = item.dataset.expirationAt || ""
    if (this.hasStatusFieldTarget) this.statusFieldTarget.checked = item.dataset.status === "true"
    this.typeFieldTargets.forEach((radio) => {
      radio.checked = radio.value === (item.dataset.notificationType || "")
    })
    this.kindFieldTargets.forEach((radio) => {
      radio.checked = radio.value === (item.dataset.kind || "")
    })
    this.#hideEditErrors()
    $(this.editModalTarget).modal("show")
  }

  save(event) {
    event.preventDefault()
    if (!this.hasEditFormTarget || this.submitting) return

    this.submitting = true
    this.#hideEditErrors()

    this.#request(this.editFormTarget.action, {
      method: "PATCH",
      body: new FormData(this.editFormTarget),
    })
      .then((data) => {
        if (!data.success) {
          this.#showEditError(data.message || "Failed to update service notification")
          return
        }

        this.#updateItem(this.pendingItem, data)
        $(this.editModalTarget).modal("hide")
        this.pendingItem = null
      })
      .catch(() => {
        this.#showEditError("An error occurred during request")
      })
      .finally(() => {
        this.submitting = false
      })
  }

  openRemove(event) {
    event.preventDefault()
    event.currentTarget.blur()
    const item = this.#itemFromEvent(event)
    if (!item || !this.hasConfirmModalTarget) return

    this.pendingItem = item
    if (this.hasConfirmNameTarget) {
      this.confirmNameTarget.textContent = item.dataset.title || "this notification"
    }
    this.#hideConfirmErrors()
    $(this.confirmModalTarget).modal("show")
  }

  confirmRemove(event) {
    event.preventDefault()
    const item = this.pendingItem
    if (!item || this.submitting) return

    this.submitting = true
    this.#hideConfirmErrors()
    this.#request(item.dataset.url, { method: "DELETE" })
      .then((data) => {
        if (!data.success) {
          this.#showConfirmError(data.message || "Failed to delete service notification")
          return
        }

        if (this.dataTable) {
          this.dataTable.row(item).remove().draw()
        } else {
          item.remove()
        }
        $(this.confirmModalTarget).modal("hide")
        this.pendingItem = null
      })
      .catch(() => {
        this.#showConfirmError("An error occurred during request")
      })
      .finally(() => {
        this.submitting = false
      })
  }

  #initDataTable() {
    this.dataTable = createTurboDataTable(this.tableTarget, {
      paging: false,
      searching: false,
      info: false,
      lengthChange: false,
      autoWidth: false,
      order: [[0, "asc"]],
      columnDefs: [
        { targets: 5, orderable: false, searchable: false, width: "70px" },
      ],
      language: {
        emptyTable: "No service notifications for this account",
      },
    })
  }

  #itemFromEvent(event) {
    return event.currentTarget.closest("[data-account-managements--service-notifications-target='item']")
  }

  #updateItem(item, data) {
    if (!item) return

    const type = data.notification_type || ""
    const kind = data.kind || ""
    const expiration = data.expiration_at || ""
    const status = !!data.status

    item.dataset.title = data.title || ""
    item.dataset.message = data.message || ""
    item.dataset.notificationType = type
    item.dataset.kind = kind
    item.dataset.expirationAt = expiration
    item.dataset.status = status ? "true" : "false"

    const titleEl = item.querySelector(".account-service-notification-title")
    if (titleEl) titleEl.textContent = data.title || ""

    const typeCell = item.querySelector(".account-service-notification-type")
    if (typeCell) typeCell.dataset.order = type
    const badge = item.querySelector(".account-service-notification-type-badge")
    if (badge) {
      badge.className = `account-service-notification-type-badge is-${type}`
      badge.textContent = TYPE_LABELS[type] || type
    }

    const kindEl = item.querySelector(".account-service-notification-kind")
    if (kindEl) {
      kindEl.textContent = KIND_LABELS[kind] || kind
      kindEl.dataset.order = kind
    }

    const expirationEl = item.querySelector(".account-service-notification-expiration")
    if (expirationEl) {
      expirationEl.textContent = expiration || "-"
      expirationEl.dataset.order = expiration
    }

    const statusEl = item.querySelector(".account-service-notification-status")
    if (statusEl) statusEl.textContent = status ? "Active" : "Inactive"

    if (this.dataTable) this.dataTable.row(item).invalidate().draw(false)
  }

  #initDatepicker() {
    if (!this.hasExpirationFieldTarget) return

    const $picker = $(this.expirationFieldTarget)
    if ($picker.data("xdsoft_datetimepicker")) $picker.datetimepicker("destroy")
    $picker.datetimepicker({
      format: "Y-m-d H:i:s",
      lang: "en",
      step: 1,
    })
  }

  #destroyDatepicker() {
    if (!this.hasExpirationFieldTarget) return

    const $picker = $(this.expirationFieldTarget)
    if ($picker.data("xdsoft_datetimepicker")) $picker.datetimepicker("destroy")
  }

  #request(url, options) {
    const csrf = document.querySelector("meta[name='csrf-token']")?.content

    return fetch(url, {
      ...options,
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrf || "",
        "X-Requested-With": "XMLHttpRequest",
        ...(options.headers || {}),
      },
      credentials: "same-origin",
    }).then((response) => {
      return response.json().catch(() => null).then((payload) => {
        if (!payload) throw new Error("Request failed")
        return payload
      })
    })
  }

  #hideEditErrors() {
    if (!this.hasEditErrorsTarget) return
    this.editErrorsTarget.style.display = "none"
  }

  #showEditError(message) {
    showBlock("#account-service-notification-edit-errors", message)
  }

  #hideConfirmErrors() {
    if (!this.hasConfirmErrorsTarget) return
    this.confirmErrorsTarget.style.display = "none"
  }

  #showConfirmError(message) {
    showBlock("#account-service-notification-confirm-errors", message)
  }
}
